import { useLocation } from 'react-router-dom';
import LegacyGonePage from '@/pages/LegacyGonePage';
import LegacyRedirect from '@/pages/LegacyRedirect';
import NotFoundPage from '@/pages/NotFoundPage';

/**
 * PRESERVED LEGACY URLS — These take precedence over all legacy retirement rules.
 * Any URL in this list is intentionally kept indexable and must NOT receive noindex.
 * The generator article has its own dedicated Route in App.js, so it won't reach
 * this component, but we include it here for safety/documentation.
 */
const PRESERVED_URLS = new Set([
  '/2025/11/26/how-to-connect-a-portable-generator-to-your-home-main-panel',
  '/2025/11/26/how-to-connect-a-portable-generator-to-your-home-main-panel/',
]);

/**
 * CATEGORY C — Verified unrelated legacy URLs → noindex Gone page
 * These are EXACT paths confirmed via GSC export.
 */
const CATEGORY_C_GONE = new Set([
  '/2024/11/19/jeep-avenger-ev-its-a-jeep/',
  '/2024/11/19/jeep-avenger-ev-its-a-jeep',
  '/2025/10/28/nintendo-switch-2-vs-switch-usb-c-port-key-connectivity-differences/',
  '/2025/10/28/nintendo-switch-2-vs-switch-usb-c-port-key-connectivity-differences',
  '/2024/06/24/ge-profile-all-in-one-washer-dryer-review/',
  '/2024/06/24/ge-profile-all-in-one-washer-dryer-review',
  '/2025/11/17/the-ultimate-fat-tire-folding-e-bike-meet-the-jasionbike-hunter-pro/',
  '/2025/11/17/the-ultimate-fat-tire-folding-e-bike-meet-the-jasionbike-hunter-pro',
  '/2024/09/08/my-volvo-ex30-cleantechnica/',
  '/2024/09/08/my-volvo-ex30-cleantechnica',
  '/2025/03/26/felotric-fold-1-a-folding-electronic-bike-you-get-properly-cleantechnica-has-been-tested/',
  '/2025/03/26/felotric-fold-1-a-folding-electronic-bike-you-get-properly-cleantechnica-has-been-tested',
]);

/**
 * LEGACY REDIRECTS — URLs with a genuine current equivalent
 */
const LEGACY_REDIRECTS = {
  '/summer-pool-wiring--outdoor-lighting-safety-in-nashville': '/services/pool-spa-wiring-nashville',
  '/contact-us/': '/contact',
  '/about-us/': '/about',
  '/faq/': '/faq',
};

/**
 * WordPress legacy PREFIX patterns → noindex Gone page
 * These are infrastructure patterns, not individual articles.
 */
const LEGACY_GONE_PREFIXES = [
  '/tag/',
  '/category/',
  '/author/',
  '/feed/',
  '/wp-content/',
  '/wp-includes/',
  '/wp-json/',
  '/page/',
];

/**
 * Exact WordPress infrastructure paths → noindex Gone page
 */
const LEGACY_GONE_EXACT = new Set([
  '/wp-login.php',
  '/wp-admin',
  '/wp-admin/',
]);

/**
 * Date-based prefixes that contain legacy content.
 * IMPORTANT: Preserved URLs under these prefixes are handled FIRST.
 */
const LEGACY_DATE_PREFIXES = ['/2023/', '/2024/', '/2025/'];

/**
 * LegacyCatchAll — Determines the correct treatment for unmatched paths.
 * 
 * Priority order:
 * 1. Preserved URLs → should never reach here (have dedicated Routes)
 * 2. Category C exact matches → LegacyGonePage (noindex)
 * 3. Legacy redirects → LegacyRedirect (JS redirect)
 * 4. WordPress prefix patterns → LegacyGonePage (noindex)
 * 5. WordPress exact paths → LegacyGonePage (noindex)
 * 6. Date-based prefixes (excluding preserved) → LegacyGonePage (noindex)
 * 7. /blog/page/* deep pagination → LegacyGonePage (noindex)
 * 8. Everything else → NotFoundPage (noindex)
 */
export default function LegacyCatchAll() {
  const location = useLocation();
  const path = location.pathname;

  // 1. Preserved URLs — safety check (shouldn't reach here)
  if (PRESERVED_URLS.has(path)) {
    return null; // Should be handled by dedicated Route
  }

  // 2. Category C exact matches
  if (CATEGORY_C_GONE.has(path)) {
    return <LegacyGonePage />;
  }

  // 3. Legacy redirects
  const pathNoTrail = path.endsWith('/') ? path.slice(0, -1) : path;
  if (LEGACY_REDIRECTS[path] || LEGACY_REDIRECTS[pathNoTrail]) {
    return <LegacyRedirect to={LEGACY_REDIRECTS[path] || LEGACY_REDIRECTS[pathNoTrail]} />;
  }

  // 4. WordPress prefix patterns
  for (const prefix of LEGACY_GONE_PREFIXES) {
    if (path.startsWith(prefix)) {
      return <LegacyGonePage />;
    }
  }

  // 5. WordPress exact paths
  if (LEGACY_GONE_EXACT.has(path) || LEGACY_GONE_EXACT.has(pathNoTrail)) {
    return <LegacyGonePage />;
  }

  // 6. Date-based prefixes (excluding preserved)
  for (const datePrefix of LEGACY_DATE_PREFIXES) {
    if (path.startsWith(datePrefix)) {
      return <LegacyGonePage />;
    }
  }

  // 7. /blog/page/* deep pagination
  if (path.startsWith('/blog/page/')) {
    return <LegacyGonePage />;
  }

  // 8. Default: genuinely unknown URL
  return <NotFoundPage />;
}
